import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  AnomalyPolygonMapClusterDto,
  AnomalyPolygonMapDetailDto,
  AnomalyPolygonMapDetailsDto,
  AnomalyPolygonMapRegionAggregateDto,
  AnomalyPolygonsService,
  FieldFilter as SatelliteFieldFilter,
  LocalizationString,
} from '@6grain/masofa-monolith-api-client/satellite-indices';
import {Region} from '@6grain/masofa-monolith-api-client/dictionaries';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {GisCircleMarker} from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import {Marker} from '@components/openstreetmap/interfaces/marker-layer.interface';
import {GisPolygon} from '@components/openstreetmap/interfaces/gis-component.interfaces';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {createRegionCircleMarker} from '@app/utils/circle-marker.util';
import {
  BID_MAP_CLUSTER_CIRCLE_CLICK_ZOOM,
  BID_MAP_ZOOM_AGGREGATE_MAX,
  BID_MAP_ZOOM_CLUSTER_BAND_MAX,
  BID_MAP_ZOOM_CLUSTER_MIN,
} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-zoom.config';
import {
  BID_FIELD_POLYGON_FILL,
  BID_FIELD_POLYGON_STROKE,
  BID_MAP_ACCENT,
  BID_MAP_STROKE,
} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-colors';
import {
  ANOMALY_FIELD_OUTLINE_DASH,
  ANOMALY_FIELD_OUTLINE_STROKE,
  resolveAnomalyPolygonColor,
  translateKeyForAnomalyType,
  translateKeyForProductSource,
} from '@app/pages/crop-monitoring/pages/anomalies/anomaly-type-colors';
import {ServerTranslateService} from '@services/server-translate.service';

/** When API sends parcel boundary + anomaly lat/lng, anomaly footprint radius on map (meters). */
export const ANOMALY_MAP_POINT_BUFFER_METERS = 42;
/** Safety cutoff: skip polygons larger than 1000 km². */
export const ANOMALY_MAP_MAX_POLYGON_AREA_M2 = 1_000_000_000;
/** Safety cutoff: skip polygons wider/taller than 300 km. */
export const ANOMALY_MAP_MAX_POLYGON_SPAN_M = 300_000;

/** Approximate geodesic circle as a lat/lng ring (visual footprint when API gives point + field polygon). */
export function anomalyApproxCircleRing(lat: number, lng: number, radiusM: number, segments = 36): [number, number][] {
  const earthRadius = 6378137;
  const ring: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * 2 * Math.PI;
    const dx = radiusM * Math.cos(t);
    const dy = radiusM * Math.sin(t);
    const dLat = (dy / earthRadius) * (180 / Math.PI);
    const cosLat = Math.cos((lat * Math.PI) / 180);
    const dLng = cosLat > 1e-6 ? ((dx / (earthRadius * cosLat)) * 180) / Math.PI : 0;
    ring.push([lat + dLat, lng + dLng]);
  }
  return ring;
}

function readExtraJson(dto: AnomalyPolygonMapDetailDto, keys: string[]): string | null {
  const rec = dto as Record<string, unknown>;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'string' && v.trim()) {
      return v;
    }
  }
  return null;
}

function normalizeDetailRows(details: unknown): any[] {
  if (!details || typeof details !== 'object') {
    return [];
  }
  const d = details as AnomalyPolygonMapDetailsDto;
  if (!Array.isArray(d.anomalies)) {
    return [];
  }
  return d.anomalies.map(a => ({
    anomalyPolygon: {...a},
    fieldName: a.fieldName ?? '',
    fieldId: a.fieldId,
  }));
}

@Injectable({providedIn: 'root'})
export class AnomalyMapManagerService {
  private readonly anomalyPolygons = inject(AnomalyPolygonsService);
  private readonly regionManager = inject(RegionManagerService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly serverTranslate = inject(ServerTranslateService);

  private allRegions: Region[] = [];
  private regionsLoading: Promise<void> | null = null;

  loadRegionAggregates(filters: any[], mapRef: OpenStreetMapComponent | null): Observable<GisCircleMarker[]> {
    return this.anomalyPolygons
      .satelliteAnomalyPolygonsGetMapRegionAggregatesPost({
        query: {filters: filters as SatelliteFieldFilter[]},
        level: 2,
      } as Parameters<AnomalyPolygonsService['satelliteAnomalyPolygonsGetMapRegionAggregatesPost']>[0])
      .pipe(
        map(rows =>
          (rows ?? [])
            .map((r, i) => this.aggregateToCircle(r, i, mapRef))
            .filter((c): c is GisCircleMarker => c != null),
        ),
      );
  }

  loadClusters(
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: any[],
    mapRef: OpenStreetMapComponent | null,
  ): Observable<{circles: GisCircleMarker[]; pointMarkers: Marker[]}> {
    return this.anomalyPolygons
      .satelliteAnomalyPolygonsGetMapClustersPost({
        minLat: bounds.minLat,
        maxLat: bounds.maxLat,
        minLng: bounds.minLng,
        maxLng: bounds.maxLng,
        query: {filters: filters as SatelliteFieldFilter[]},
      })
      .pipe(
        map(list => {
          const circles: GisCircleMarker[] = [];
          const rows = list ?? [];
          for (let i = 0; i < rows.length; i++) {
            const circle = this.clusterToCircle(rows[i], i, mapRef);
            if (circle) {
              circles.push(circle);
            }
          }
          return {circles, pointMarkers: [] as Marker[]};
        }),
      );
  }

  loadDetails(
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: any[],
  ): Observable<AnomalyPolygonMapDetailsDto> {
    return this.anomalyPolygons.satelliteAnomalyPolygonsGetMapDetailsPost({
      minLat: bounds.minLat,
      maxLat: bounds.maxLat,
      minLng: bounds.minLng,
      maxLng: bounds.maxLng,
      query: {filters: filters as SatelliteFieldFilter[]},
    });
  }

  loadTotalCount(filters: any[]): Observable<number> {
    return this.anomalyPolygons
      .satelliteAnomalyPolygonsGetTotalCountPost({
        filters: filters as SatelliteFieldFilter[],
      } as Parameters<AnomalyPolygonsService['satelliteAnomalyPolygonsGetTotalCountPost']>[0])
      .pipe(map(v => Number(v ?? 0)));
  }

  detailsToLayers(details: unknown): {polygons: GisPolygon[]; markers: Marker[]} {
    // Best-effort: warm up region dictionary for popup rendering (sync popup uses cached list).
    this.ensureRegionsLoaded();
    const rows = normalizeDetailRows(details);
    return this.rowsToLayers(rows);
  }

  /**
   * Field parcel outline from GetMapDetails rows: {@code fieldPolygonJson} on each anomaly (no crop-monitoring Field/GetByQuery).
   */
  loadFieldOutlinePolygonsForAnomalyDetails(
    dto: AnomalyPolygonMapDetailsDto | null | undefined,
  ): Observable<GisPolygon[]> {
    const anomalies = dto?.anomalies ?? [];
    const polygons: GisPolygon[] = [];
    const seenFieldIds = new Set<string>();

    for (let index = 0; index < anomalies.length; index++) {
      const a = anomalies[index];
      const fidRaw = a.fieldId != null ? String(a.fieldId).trim() : '';
      if (fidRaw && seenFieldIds.has(fidRaw)) {
        continue;
      }
      const outline = this.fieldOutlineFromMapDetailAnomaly(a, index);
      if (!outline) {
        continue;
      }
      if (fidRaw) {
        seenFieldIds.add(fidRaw);
      }
      polygons.push(outline);
    }

    return of(polygons);
  }

  private fieldOutlineFromMapDetailAnomaly(
    anomaly: AnomalyPolygonMapDetailDto,
    index: number,
  ): GisPolygon | null {
    const pj = readExtraJson(anomaly, ['fieldPolygonJson']);
    if (!pj) {
      return null;
    }
    const fid =
      anomaly.fieldId != null && String(anomaly.fieldId).trim()
        ? String(anomaly.fieldId).trim()
        : `idx-${index}`;
    try {
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(pj);
      if (coordinates.length < 3) {
        return null;
      }
      const areaM2 = polygonAreaM2FromLatLngRing(coordinates);
      const span = polygonSpanMFromLatLngRing(coordinates);
      if (
        areaM2 > ANOMALY_MAP_MAX_POLYGON_AREA_M2 ||
        span.widthM > ANOMALY_MAP_MAX_POLYGON_SPAN_M ||
        span.heightM > ANOMALY_MAP_MAX_POLYGON_SPAN_M
      ) {
        return null;
      }
      const name = anomaly.fieldName != null && String(anomaly.fieldName).trim() ? String(anomaly.fieldName).trim() : '';
      return {
        id: `anomaly-field-mapdetail-${fid}-${index}`,
        name,
        type: 'monitoring',
        coordinates,
        strokeColor: ANOMALY_FIELD_OUTLINE_STROKE,
        color: ANOMALY_FIELD_OUTLINE_STROKE,
        fillColor: BID_FIELD_POLYGON_FILL,
        dashArray: ANOMALY_FIELD_OUTLINE_DASH,
        fill: true,
        fillOpacity: 0.15,
        opacity: 0.95,
        strokeWidth: 2,
        interactive: false,
        // Keep field polygons below anomaly patches.
        pane: 'anomalyFieldOutlinePane',
        ...(name ? {label: escapeHtml(name), labelColor: '#1a1a1a'} : {}),
        data: {fieldId: fid, anomaly},
      };
    } catch {
      return null;
    }
  }

  private rowsToLayers(rows: any[]): {polygons: GisPolygon[]; markers: Marker[]} {
    const polygons: GisPolygon[] = [];
    const markers: Marker[] = [];
    let idx = 0;
    for (const row of rows) {
      const dto = row?.anomalyPolygon ?? row;
      const pjAnomalyExplicit =
        readExtraJson(dto, ['anomalyPolygonJson', 'patchPolygonJson', 'damagePolygonJson']) ?? null;

      const combined = dto.polygonJson != null ? String(dto.polygonJson).trim() : '';
      const latRaw = dto.lat;
      const lngRaw = dto.lng;
      const lat = latRaw != null ? Number(latRaw) : NaN;
      const lng = lngRaw != null ? Number(lngRaw) : NaN;
      const hasPoint = !Number.isNaN(lat) && !Number.isNaN(lng);

      try {
        const color = resolveAnomalyPolygonColor(dto);
        const idBase = dto.id ?? dto.fieldId ?? idx;

        /** Anomaly footprint: explicit JSON, then {@code polygonJson} (WKT / GeoJSON). */
        let anomalyCoords: [number, number][] | null = null;
        if (pjAnomalyExplicit) {
          anomalyCoords = PolygonConverterUtil.polygonJsonToCoordinates(pjAnomalyExplicit);
        }
        if ((!anomalyCoords || anomalyCoords.length < 3) && combined) {
          anomalyCoords = PolygonConverterUtil.polygonJsonToCoordinates(combined);
        }

        let filledCoords = anomalyCoords && anomalyCoords.length >= 3 ? anomalyCoords : null;
        let usedCircleOnly = false;
        if (!filledCoords && hasPoint) {
          filledCoords = anomalyApproxCircleRing(lat, lng, ANOMALY_MAP_POINT_BUFFER_METERS);
          usedCircleOnly = true;
        }

        const isPolygonAreaAllowed = (coordinates: [number, number][]): boolean => {
          if (coordinates.length < 3) {
            return false;
          }
          const areaM2 = polygonAreaM2FromLatLngRing(coordinates);
          const span = polygonSpanMFromLatLngRing(coordinates);
          if (
            areaM2 > ANOMALY_MAP_MAX_POLYGON_AREA_M2 ||
            span.widthM > ANOMALY_MAP_MAX_POLYGON_SPAN_M ||
            span.heightM > ANOMALY_MAP_MAX_POLYGON_SPAN_M
          ) {
            return false;
          }
          return true;
        };

        const pushAnomalyFilled = (coordinates: [number, number][]) => {
          if (!isPolygonAreaAllowed(coordinates)) {
            return false;
          }
          const popupHtml = this.buildAnomalyPopupHtml(row, dto);
          polygons.push({
            id: `anomaly-patch-${idBase}-${idx}`,
            name: row?.fieldName ?? '',
            type: 'monitoring',
            coordinates,
            strokeColor: color,
            color,
            fillColor: color,
            fillOpacity: 0.42,
            strokeWidth: 2,
            fill: true,
            pane: 'anomalyHighlightPane',
            data: row,
            popup: popupHtml,
            openPopupOnHover: true,
            onClick: () => this.navigateToFieldIfPossible(row, dto),
          });
          return true;
        };

        if (filledCoords && filledCoords.length >= 3) {
          const polygonWasAdded = pushAnomalyFilled(filledCoords);
          if (polygonWasAdded) {
            const center = PolygonConverterUtil.getPolygonCenter(filledCoords);
            const pinLat = usedCircleOnly && hasPoint ? lat : center?.lat;
            const pinLng = usedCircleOnly && hasPoint ? lng : center?.lng;
            if (pinLat != null && pinLng != null && !Number.isNaN(pinLat) && !Number.isNaN(pinLng)) {
              markers.push({
                id: `anomaly-mrk-${idBase}-${idx}`,
                lat: pinLat,
                lng: pinLng,
                color,
                withCluster: false,
                openPopupOnHover: true,
                data: row,
                popup: this.buildAnomalyPopupHtml(row, dto),
                onClick: () => this.navigateToFieldIfPossible(row, dto),
              });
            }
          }
        }
      } catch {
        /* skip row */
      }
      idx++;
    }
    return {polygons, markers};
  }

  private navigateToFieldIfPossible(row: any, ap: any): void {
    const fieldId = row?.fieldId ?? ap?.fieldId;
    if (fieldId != null && String(fieldId).trim() !== '') {
      void this.router.navigate([`/electronic-field-map/fields/${fieldId}/general`]);
    }
  }

  /** Leaflet HTML popup (same layout/CSS roots as {@link BidMapManagerService} bid pins). */
  private buildAnomalyPopupHtml(row: any, dto: any): string {
    const typeLabel = this.resolveAnomalyTypeLabel(row, dto);
    const badgeBg = resolveAnomalyPolygonColor(dto);
    const badgeFg = contrastTextForHexBackground(badgeBg);
    const badge = `<span class="bid-map-popup__badge" style="background-color:${badgeBg};color:${badgeFg}">${escapeHtml(typeLabel)}</span>`;

    const fieldCaption = escapeHtml(this.translate.instant('ANOMALIES_TABLE_FIELD'));
    const fieldRaw = String(row?.fieldName ?? dto?.fieldName ?? '').trim();
    const fieldRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${fieldCaption}</span><span class="bid-map-popup__row-value">${fieldRaw ? escapeHtml(fieldRaw) : escapeHtml('–')}</span></div>`;

    const cropCaption = escapeHtml(this.translate.instant('ANOMALIES_TABLE_CROP'));
    const cropLine = this.formatAnomalyCrop(row, dto);
    const cropRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${cropCaption}</span><span class="bid-map-popup__row-value">${cropLine ? escapeHtml(cropLine) : escapeHtml('–')}</span></div>`;

    const regionCaption = escapeHtml(this.translate.instant('ANOMALIES_TABLE_REGION'));
    const regionLine = this.formatAnomalyRegion(row, dto);
    const regionRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${regionCaption}</span><span class="bid-map-popup__row-value">${regionLine ? escapeHtml(regionLine) : escapeHtml('–')}</span></div>`;

    const sourceCaption = escapeHtml(this.translate.instant('ANOMALIES_TABLE_SOURCE'));
    const sourceLabel = this.resolveProductSourceLabel(row, dto);
    const sourceRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${sourceCaption}</span><span class="bid-map-popup__row-value">${escapeHtml(sourceLabel)}</span></div>`;

    return `<div class="bid-map-popup bid-map-popup--bid">${badge}${fieldRow}${cropRow}${regionRow}${sourceRow}</div>`;
  }

  private localizationLine(holder: unknown, key: string): string {
    if (!holder || typeof holder !== 'object') {
      return '';
    }
    const rec = holder as Record<string, unknown>;
    const raw = rec[key];
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }
    const t = this.serverTranslate.translate(holder, key);
    if (typeof t === 'string' && t.trim()) {
      return t.trim();
    }
    if (Array.isArray(t)) {
      return t.filter(s => s != null && String(s).trim()).join(', ');
    }
    return '';
  }

  private formatAnomalyCrop(row: any, dto: any): string {
    const fromSingle =
      this.localizationLine(row, 'cropName') ||
      this.localizationLine(dto, 'cropName') ||
      (typeof dto?.crop === 'string' && dto.crop.trim() ? dto.crop.trim() : '');
    if (fromSingle) {
      return fromSingle;
    }

    const cropNames = dto?.cropNames ?? row?.cropNames;
    return this.formatLocalizationList(cropNames, 'names');
  }

  /** Prefers aggregate-style {@code regionNames}, then localized {@code regionName} / nested {@code region.names}. */
  private formatAnomalyRegion(row: any, dto: any): string {
    const fromAgg = this.pickRegionName(dto?.regionNames ?? row?.regionNames);
    if (fromAgg.trim()) {
      return fromAgg.trim();
    }
    const direct =
      this.localizationLine(row, 'regionName') ||
      this.localizationLine(dto, 'regionName');
    if (direct) {
      return direct;
    }
    const district =
      this.localizationLine(dto?.region, 'names') ||
      this.localizationLine(row?.region, 'names');
    const parent =
      this.localizationLine(dto?.parentRegion, 'names') ||
      this.localizationLine(row?.parentRegion, 'names') ||
      this.localizationLine(dto?.regionParent, 'names') ||
      this.localizationLine(row?.regionParent, 'names');
    if (parent && district) {
      return `${parent} / ${district}`;
    }
    const fromId = dto?.regionId ?? row?.regionId;
    if (parent || district) {
      return parent || district || '';
    }
    const id = fromId != null && String(fromId).trim() ? String(fromId).trim() : '';
    if (!id) {
      return '';
    }
    const resolved = this.regionLabelFromId(id);
    return resolved || '';
  }

  private ensureRegionsLoaded(): void {
    if (this.allRegions.length > 0 || this.regionsLoading) {
      return;
    }
    this.regionsLoading = (async () => {
      try {
        this.allRegions = await this.regionManager.getRegions();
      } finally {
        this.regionsLoading = null;
      }
    })();
  }

  private regionLabelFromId(regionId: string): string {
    this.ensureRegionsLoaded();
    if (!this.allRegions.length) {
      return '';
    }
    const region = this.allRegions.find(r => r.id === regionId);
    if (!region) {
      return '';
    }
    const parentId = (region as any)?.parentId;
    const parent = parentId && parentId !== region.id ? this.allRegions.find(r => r.id === parentId) : undefined;
    const districtName = this.serverTranslate.translate(region, 'names');
    const parentName = parent ? this.serverTranslate.translate(parent, 'names') : null;
    const d = Array.isArray(districtName) ? districtName.join(', ') : (districtName ?? '');
    const p = Array.isArray(parentName) ? parentName.join(', ') : (parentName ?? '');
    if (p && d) {
      return `${p} / ${d}`;
    }
    return d || p || '';
  }

  private formatLocalizationList(value: unknown, keyBase: string): string {
    if (!value) {
      return '';
    }
    if (Array.isArray(value)) {
      const parts: string[] = [];
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) {
          parts.push(item.trim());
          continue;
        }
        const t = this.serverTranslate.translate(item as any, keyBase);
        if (typeof t === 'string' && t.trim()) {
          parts.push(t.trim());
        } else if (Array.isArray(t)) {
          for (const s of t) {
            if (s && String(s).trim()) {
              parts.push(String(s).trim());
            }
          }
        }
      }
      return [...new Set(parts)].join(', ');
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    const localized = this.pickLocalizedText(value as Record<string, unknown>);
    if (localized) {
      return localized;
    }
    const one = this.serverTranslate.translate(value as any, keyBase);
    if (typeof one === 'string') {
      return one.trim();
    }
    if (Array.isArray(one)) {
      return one.filter(s => s && String(s).trim()).join(', ');
    }
    return '';
  }

  private resolveAnomalyTypeLabel(row: any, dto: any): string {
    const raw =
      dto?.anomalyType ?? dto?.type ?? dto?.codeFromAiServices ?? dto?.codeFromEnum ??
      row?.anomalyType ?? row?.type ?? row?.codeFromAiServices ?? row?.codeFromEnum;
    const key = translateKeyForAnomalyType(raw);
    if (key) {
      const translated = this.translate.instant(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }
    return '—';
  }

  private resolveProductSourceLabel(row: any, dto: any): string {
    const raw = dto?.productSourceType ?? dto?.source ?? dto?.productSource ?? row?.productSourceType ?? row?.source ?? row?.productSource;
    const key = translateKeyForProductSource(raw);
    if (key) {
      const translated = this.translate.instant(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }
    return '—';
  }

  private pickLocalizedText(value: Record<string, unknown>): string {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return '';
    }
    const names = value as Record<string, unknown>;
    const lang = this.translate.currentLang || this.translate.defaultLang || 'en-US';
    const short = lang.split('-')[0] ?? '';
    const candidates = [lang, short, 'en-US', 'en', 'ru-RU', 'ru', 'uz-Latn-UZ', 'uz'];
    for (const key of candidates) {
      if (!key) {
        continue;
      }
      const found = names[key];
      if (typeof found === 'string' && found.trim()) {
        return found.trim();
      }
    }
    for (const v of Object.values(names)) {
      if (typeof v === 'string' && v.trim()) {
        return v.trim();
      }
    }
    return '';
  }

  private aggregateToCircle(
    a: AnomalyPolygonMapRegionAggregateDto,
    index: number,
    mapRef: OpenStreetMapComponent | null,
  ): GisCircleMarker | null {
    const lat = a.centerRegion?.lat ?? undefined;
    const lng = a.centerRegion?.lng ?? undefined;
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    const regionId =
      a.regionId != null && String(a.regionId).length ? String(a.regionId) : `idx-${index}`;
    const count = a.count ?? 0;
    if (count < 1) {
      return null;
    }
    const name = this.pickRegionName(a.regionNames);
    const cache = this.regionManager.getRegionPolygonsCache().get(regionId);

    if (cache?.polygon && count > 0) {
      const poly = cache.polygon;
      const marker = createRegionCircleMarker(regionId, count, cache.center, poly, BID_MAP_ACCENT, mapRef ?? undefined);
      marker.fillColor = BID_MAP_ACCENT;
      marker.strokeColor = BID_MAP_STROKE;
      marker.minZoom = 0;
      marker.maxZoom = BID_MAP_ZOOM_AGGREGATE_MAX;
      marker.showByZoomLevel = true;
      marker.type = 'anomaly-region-count';
      if (name && marker.data) {
        marker.data = {
          regionId: marker.data.regionId,
          regionName: name,
          fieldCount: marker.data.fieldCount,
          level: marker.data.level,
        };
      }
      marker.onClick = () => {
        const lc = mapRef?.getLayersController();
        if (!lc) {
          return;
        }
        lc.centerToPoint(marker.center[0], marker.center[1], BID_MAP_ZOOM_CLUSTER_MIN, true);
      };
      return marker;
    }

    const tooltipName = name || regionId;
    return {
      id: `anomaly-region-${regionId}`,
      center: [lat, lng],
      radius: 30,
      count,
      fillColor: BID_MAP_ACCENT,
      strokeColor: BID_MAP_STROKE,
      fillOpacity: 0.6,
      strokeWidth: 2,
      type: 'anomaly-region-count',
      minZoom: 0,
      maxZoom: BID_MAP_ZOOM_AGGREGATE_MAX,
      showByZoomLevel: true,
      data: {
        regionId,
        regionName: tooltipName,
        fieldCount: count,
      },
      onClick: circle => {
        const lc = mapRef?.getLayersController();
        if (!lc) {
          return;
        }
        lc.centerToPoint(circle.center[0], circle.center[1], BID_MAP_ZOOM_CLUSTER_MIN, true);
      },
    };
  }

  private clusterToCircle(
    c: AnomalyPolygonMapClusterDto,
    index: number,
    mapRef: OpenStreetMapComponent | null,
  ): GisCircleMarker | null {
    const lat = c.lat;
    const lng = c.lng;
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    const count = c.count ?? 0;
    if (count < 1) {
      return null;
    }
    return {
      id: `anomaly-cluster-${index}-${lat}-${lng}`,
      center: [lat, lng],
      count,
      fillColor: BID_MAP_ACCENT,
      strokeColor: BID_MAP_STROKE,
      fillOpacity: 0.65,
      showByZoomLevel: true,
      minZoom: BID_MAP_ZOOM_CLUSTER_MIN,
      maxZoom: BID_MAP_ZOOM_CLUSTER_BAND_MAX,
      type: 'anomaly-cluster',
      data: {regionId: 'anomaly-cluster', regionName: '', fieldCount: count},
      onClick: () => {
        const lc = mapRef?.getLayersController();
        lc?.centerToPoint(lat, lng, BID_MAP_CLUSTER_CIRCLE_CLICK_ZOOM, true);
      },
    };
  }

  private pickRegionName(names: LocalizationString | null | undefined): string {
    if (!names || typeof names !== 'object') {
      return '';
    }
    const lang = this.translate.currentLang || 'en-US';
    const fromLang = names[lang as keyof LocalizationString];
    if (typeof fromLang === 'string' && fromLang.trim()) {
      return fromLang;
    }
    const en = names['en-US'];
    if (typeof en === 'string' && en.trim()) {
      return en;
    }
    const ru = names['ru-RU'];
    if (typeof ru === 'string' && ru.trim()) {
      return ru;
    }
    const uz = names['uz-Latn-UZ'];
    if (typeof uz === 'string' && uz.trim()) {
      return uz;
    }
    return '';
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function contrastTextForHexBackground(hex: string): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) {
    return '#ffffff';
  }
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.62 ? '#111827' : '#ffffff';
}

/**
 * Approximate polygon area (m²) from a [lat,lng] ring using an equirectangular projection.
 * Accurate enough for sanity-threshold filtering.
 */
function polygonAreaM2FromLatLngRing(coords: [number, number][]): number {
  if (!Array.isArray(coords) || coords.length < 3) {
    return 0;
  }
  const ring =
    coords.length > 1 &&
    coords[0][0] === coords[coords.length - 1][0] &&
    coords[0][1] === coords[coords.length - 1][1]
      ? coords.slice(0, coords.length - 1)
      : coords;
  if (ring.length < 3) {
    return 0;
  }

  const earthRadius = 6378137;
  const toRad = (d: number): number => (d * Math.PI) / 180;
  let latSum = 0;
  let lngSum = 0;
  for (const [lat, lng] of ring) {
    latSum += lat;
    lngSum += lng;
  }
  const lat0 = toRad(latSum / ring.length);
  const lng0 = toRad(lngSum / ring.length);
  const cosLat0 = Math.cos(lat0);

  let shoelace = 0;
  for (let i = 0; i < ring.length; i++) {
    const [latA, lngA] = ring[i];
    const [latB, lngB] = ring[(i + 1) % ring.length];
    const xA = earthRadius * (toRad(lngA) - lng0) * cosLat0;
    const yA = earthRadius * (toRad(latA) - lat0);
    const xB = earthRadius * (toRad(lngB) - lng0) * cosLat0;
    const yB = earthRadius * (toRad(latB) - lat0);
    shoelace += xA * yB - xB * yA;
  }
  return Math.abs(shoelace) / 2;
}

/**
 * Approximate polygon span in meters (width east-west, height north-south).
 * Uses local equirectangular scaling around average latitude.
 */
function polygonSpanMFromLatLngRing(coords: [number, number][]): {widthM: number; heightM: number} {
  if (!Array.isArray(coords) || coords.length < 2) {
    return {widthM: 0, heightM: 0};
  }
  const ring =
    coords.length > 1 &&
    coords[0][0] === coords[coords.length - 1][0] &&
    coords[0][1] === coords[coords.length - 1][1]
      ? coords.slice(0, coords.length - 1)
      : coords;
  if (!ring.length) {
    return {widthM: 0, heightM: 0};
  }
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  let latSum = 0;
  for (const [lat, lng] of ring) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    latSum += lat;
  }
  const avgLatRad = ((latSum / ring.length) * Math.PI) / 180;
  const metersPerDegLat = 111_320;
  const metersPerDegLng = metersPerDegLat * Math.cos(avgLatRad);
  const widthM = Math.abs(maxLng - minLng) * Math.max(0, metersPerDegLng);
  const heightM = Math.abs(maxLat - minLat) * metersPerDegLat;
  return {widthM, heightM};
}
